import { Injectable } from '@angular/core';
import {ThreeService} from "./rendering/threejs/three.service";
import {ThreeInputService} from "./inputs/input-middlewares/three-input.middleware";
import {InputService} from "./inputs/services/input.service";
import {MapInputService} from "./inputs/input-middlewares/map-input.middleware";
import {CanvasService} from "./surface/canvas/canvas.service";
import {SurfaceService} from "./surface/surface.service";
import {DebrisService} from "./ambients/debris-service";
import {EventSenderService} from "./tests/event-sender.service";
import {EventsWebsocketService} from "../services/entities/events-websocket.service";
import {Store} from "@ngrx/store";
import {ActivatedRoute, Router} from "@angular/router";
import {ChatsService} from "../services/chats.service";
import {ReactionsService} from "../services/reactions.service";
import {PushNotificationService} from "../services/notifications/push-notification-service.service";
import {PwaInstallService} from "../services/notifications/pwa-install.service";
import {DoWhenReadyService, Readyables} from "../services/do-when-ready.service";
import {networkActions} from "../state/network/network.actions";
import {settings} from "../settings/settings";
import {worldActions} from "../state/world/world.actions";
import {authActions} from "../state/auth/auth.actions";
import {CanvasState} from "./surface/canvas/canvass.state";
import canvasDimensions = CanvasState.canvasDimensions;
import {ShareService} from "../services/share.service";
import * as routeSelectors from "../state/route/route.selector";
import {Vector3} from "three";
import {selectCameraTarget} from "../state/camera/camera.selector";
import {selectWorldReady} from "../state/world/world.selector";
import {cameraActions} from "../state/camera/camera.actions";
import {SearchService} from "../services/search.service";
console.log('settings at module load:', settings); // Add this at the top level

@Injectable({
  providedIn: 'root'
})
export class BinstService {

  canvasDimensions = {
    width: 4000,
    height: 4000,
  }

  coordTo: Vector3
  isWorldReady = false
  cameraTarget = new Vector3(0, 0, 0)

  constructor(
      private three: ThreeService,
      private threeInputService: ThreeInputService,
      // private postComponent: PostComponentService,
      private inputService: InputService,
      private mapInputService: MapInputService,
      private canvasService: CanvasService,
      private surfaceService: SurfaceService,
      private debrisService: DebrisService,
      private eventSenderService: EventSenderService,
      private websocketService: EventsWebsocketService,
      private store: Store,
      private router: Router,
      private route: ActivatedRoute,
      private chatsService: ChatsService,
      private reactionsService: ReactionsService,
      private pushNotificationService: PushNotificationService,
      private pwaService: PwaInstallService,
      private doWhenReady: DoWhenReadyService,
      private shareService: ShareService,
      private searchService: SearchService
  ) {
    this.doWhenReady.firstTime(this, [Readyables.auth], (user) => {
      console.log('callback fired, settings:', settings);
      console.log('callback fired, this.store:', this.store);
      if (!this.coordTo) {
        const lastCoord = JSON.parse(user.last_coord)
        this.coordTo = new Vector3(lastCoord.x, lastCoord.y, lastCoord.z)
      }
      this.store.dispatch(networkActions.enableNetwork({
        shouldEnableNetwork: !settings.offline
      }))
      if (settings.offline) {
        console.log("[BINST] Starting in offline mode")
      }
    })

    this.store
        .select(routeSelectors.selectQueryParams)
        .subscribe((params) => {
          if (params["coord"]) {
            const coord = params["coord"]
            const [x, y, z] = coord.split(":").map(Number)
            this.coordTo = new Vector3(x, y, z)
            this.navigate()
          }
        })
    this.store.select(selectCameraTarget).subscribe((target) => {
      this.cameraTarget = target
    })
    this.store.select(selectWorldReady).subscribe((isReady) => {
      if (isReady) {
        this.isWorldReady = true
        this.navigate()
      }
    })
  }


  navigate() {
    if (!this.isWorldReady) return
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ['coord']: null // Setting to null removes the parameter
      },
      queryParamsHandling: 'merge' // Keeps all other parameters
    });
    this.store.dispatch(cameraActions.targetJumped({
      target: this.coordTo,
      jumpTimeMs: 250,
      skipCheckpoint: true
    }))
  }

  startBinstView() {
    this.store.dispatch(worldActions.binstViewStarted({viewId: "main"}))
    this.store.dispatch(authActions.fetchUserStarted())
  }

  async startThree() {
    this.three.onRender(async (timeDelta) => {
      if (this.canvasService) {
        await this.canvasService.render();
      }
      if (this.surfaceService) {
        await this.surfaceService.render(timeDelta);
      }
      if (this.debrisService) {
        await this.debrisService.update(timeDelta);
      }
    });
    await this.three.startRenderLoop();
  }

  async setupCanvas(canvas: HTMLCanvasElement,) {
    await this.inputService.makeHtmlElementTouchable(canvas);
    await this.canvasService.setupCanvas(this.canvasDimensions.width, this.canvasDimensions.height);
    this.three.createScene(canvas, window, 0x10100e, false);
  }

  async startDebris() {
    await this.debrisService.setup(5);
  }

  async setupSurfaces() {
    await this.surfaceService.setup(this.canvasDimensions.width, this.canvasDimensions.height);
    await this.surfaceService.updateCamera()
  }

  async setupInputs() {
    await this.inputService.addInputMiddleware("three", this.threeInputService);
    await this.inputService.addInputMiddleware("map", this.mapInputService);
    window.addEventListener("keydown", (e) => {
      if (e.key === "Control") this.inputService.ctrlPressed = true
    });
    window.addEventListener("keyup", (e) => {
      if (e.key === "Control") this.inputService.ctrlPressed = false
    });
  }
}
